import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Download, Mail, CheckCircle2 } from "lucide-react";

const STORAGE_KEY_DISMISSED = "exit_intent_dismissed_v1";
const STORAGE_KEY_CONVERTED = "exit_intent_converted_v1";
const MOBILE_FALLBACK_DELAY_MS = 30000; // 30s of inactivity on mobile

/**
 * Exit-intent popup proposing the lead magnet PDF.
 * Triggers:
 *  - Desktop: mouse leaves viewport from the top (clientY < 0)
 *  - Mobile: 30s on page + at least one scroll (engagement signal)
 * Shown only once per session (sessionStorage) and never again if user converted (localStorage).
 */
export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const emailInputRef = useRef(null);
  const submittingRef = useRef(false);

  const isDisabled = useCallback(() => {
    if (typeof window === "undefined") return true;
    if (window.sessionStorage.getItem(STORAGE_KEY_DISMISSED)) return true;
    if (window.localStorage.getItem(STORAGE_KEY_CONVERTED)) return true;
    return false;
  }, []);

  useEffect(() => {
    if (isDisabled()) return;

    let mobileTimer = null;
    let hasScrolled = false;

    const onMouseLeave = (e) => {
      // Desktop: only top edge & primary mouse leaving the document
      if (e.clientY <= 0 && !isDisabled()) {
        setOpen(true);
      }
    };

    const onScroll = () => { hasScrolled = true; };

    const isTouch = window.matchMedia("(pointer: coarse)").matches;

    if (isTouch) {
      // Mobile fallback: open after delay if user has scrolled at least once
      mobileTimer = setTimeout(() => {
        if (hasScrolled && !isDisabled()) setOpen(true);
      }, MOBILE_FALLBACK_DELAY_MS);
      window.addEventListener("scroll", onScroll, { passive: true });
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [isDisabled]);

  const close = () => {
    // Never dismiss while a submission is in flight — this prevents the
    // component from unmounting mid-request (which would cancel the POST
    // in some browsers and hide the toast confirmation).
    if (submittingRef.current || loading) return;
    setOpen(false);
    try { window.sessionStorage.setItem(STORAGE_KEY_DISMISSED, "1"); } catch (_e) { /* noop */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Read from the DOM to survive autofill flows that don't fire onChange.
    const domValue = (emailInputRef.current?.value || "").trim();
    const effective = domValue || email.trim();
    if (!effective || !effective.includes("@") || effective.length < 5) {
      toast.error("Merci d'entrer un email valide.");
      return;
    }
    if (effective !== email) setEmail(effective);
    setLoading(true);
    submittingRef.current = true;
    try {
      await api.post("/lead-magnet/request", { email: effective });
      setSent(true);
      try { window.localStorage.setItem(STORAGE_KEY_CONVERTED, "1"); } catch (_e) { /* noop */ }
      toast.success("📕 PDF envoyé ! Vérifie ta boîte mail.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-overlay"
      onClick={close}
      className="fixed inset-0 z-[100] bg-[#1A2A40]/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
    >
      <div
        data-testid="exit-intent-popup"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#FFF5E0] border-2 border-[#1A2A40] rounded-3xl shadow-brut overflow-hidden"
      >
        {/* Decorative top band */}
        <div className="bg-[#E94A3F] text-white px-6 py-2 flex items-center justify-between">
          <span className="font-display font-black tracking-widest text-xs">🎁 ATTENDS UNE SECONDE !</span>
          <button
            onClick={close}
            data-testid="exit-intent-close"
            aria-label="Fermer"
            className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 sm:p-8">
          <div className="font-jp text-xs text-[#E94A3F] mb-1 tracking-widest">日本語、一歩ずつ</div>
          <h3 className="font-display font-black text-2xl sm:text-3xl text-[#1A2A40] leading-tight mb-3">
            Avant de partir,<br />
            <span className="text-[#E94A3F]">prends ton PDF gratuit</span> 📕
          </h3>
          <p className="text-sm text-[#4A5568] mb-5">
            <strong>50 phrases japonaises essentielles</strong> classées par situation
            : politesses, restaurant, hôtel, urgences. <strong>14 pages</strong> prêtes à imprimer.
          </p>

          {sent ? (
            <div data-testid="exit-intent-success" className="bg-white border-2 border-[#34D399] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-[#34D399]" />
                <p className="font-display font-black text-[#1A2A40] m-0">PDF envoyé !</p>
              </div>
              <p className="text-sm text-[#4A5568]">
                Vérifie ta boîte mail à <strong>{email}</strong> (pense au dossier <em>Spam</em>).
              </p>
              <button
                onClick={close}
                data-testid="exit-intent-success-close"
                className="mt-3 text-sm font-bold text-[#E94A3F] hover:underline"
              >
                Fermer →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="exit-intent-form" className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AEC0]" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  ref={emailInputRef}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  data-testid="exit-intent-email-input"
                  className="w-full pl-9 pr-3 py-3 rounded-xl border-2 border-[#1A2A40]/15 focus:outline-none focus:border-[#E94A3F] bg-white text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="exit-intent-submit"
                className="w-full bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3 rounded-xl shadow-brut-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 text-sm"
              >
                {loading ? "Envoi…" : (<><Download className="w-4 h-4" /> Recevoir mon PDF gratuit</>)}
              </button>
              <button
                type="button"
                onClick={close}
                data-testid="exit-intent-decline"
                className="w-full text-xs text-[#A0AEC0] hover:text-[#4A5568] underline pt-1"
              >
                Non merci, je passe mon tour
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
